import { uiText } from "@/lib/i18n/uiText";
import { Link } from "@/lib/router-compat";
import { Card } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { US_SPORTS } from "@/lib/usSports";
import { LEAGUE_CLUSTERS, MARKET_CLUSTERS, leaguePath } from "@/lib/seoClusters";

interface CrossSportLinksProps {
  /** Highlight/omit the current sport slug (e.g. "nfl") if on a US-sport page. */
  currentSport?: string;
  title?: string;
}

/**
 * Cross-domain internal-link block that ties soccer, US sports and market hubs
 * together. Rendered across cluster, landing and US-sports pages so crawlers
 * see a dense topical graph spanning both the soccer and US-sports keyword sets.
 */
export function CrossSportLinks({
  currentSport,
  title = "Predictions Across Every Sport",
}: CrossSportLinksProps) {
  const soccer = LEAGUE_CLUSTERS.slice(0, 6);
  const markets = MARKET_CLUSTERS.slice(0, 6);

  return (
    <section aria-label={uiText("Cross-sport predictions")} className="space-y-4">
      <h2 className="text-xl font-semibold">{title}</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">{uiText("Other Sports")}</h3>
          <ul className="space-y-2">
            {US_SPORTS.filter((s) => s.slug !== currentSport)
              .slice(0, 10)
              .map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}-predictions`}
                    className="group flex items-center justify-between text-sm hover:text-primary transition-colors"
                  >
                    <span>{uiText(s.label)}  {uiText("Predictions")}</span>
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            <li>
              <Link
                to="/explore"
                className="group flex items-center justify-between text-sm font-medium text-primary hover:underline"
              >
                <span>{uiText("All sports →")}</span>
              </Link>
            </li>
          </ul>
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">{uiText("Football / Soccer")}</h3>
          <ul className="space-y-2">
            {soccer.map((c) => (
              <li key={c.slug}>
                <Link
                  to={leaguePath(c.slug)}
                  className="group flex items-center justify-between text-sm hover:text-primary transition-colors"
                >
                  <span>{uiText(c.label)}</span>
                  <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">{uiText("Betting Markets")}</h3>
          <ul className="space-y-2">
            {markets.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/markets/${c.slug}`}
                  className="group flex items-center justify-between text-sm hover:text-primary transition-colors"
                >
                  <span>{uiText(c.label)}</span>
                  <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </section>
  );
}
